/**
 * Monta a folha A4: página em branco + unidades carimbadas nos slots
 * ocupados + linhas de corte + overlay (texto/QR) por cima.
 *
 * Origem PDF = canto inferior esquerdo (igual ReportLab / pdf-lib).
 *
 * As units podem carregar paths fora da caixa (crop marks do SEM-TEXTO A4
 * vazam no extract). Por isso o carimbo é clipado à ARTWORK_LAYOUT do slot;
 * as linhas de corte vêm só de drawCutMarks (retas, sem o −0,21 pt legado).
 */
import {
  PDFDocument,
  pushGraphicsState,
  popGraphicsState,
  moveTo,
  lineTo,
  closePath,
  clip,
  endPath,
} from "pdf-lib";
import { ARTWORK_LAYOUT } from "./labelAssets";
import { PAGE } from "./layout/constants";
import { slotForIndex } from "./sheetComposer";
import { fetchUnitTemplateBytes } from "./labelTemplates";
import { drawCutMarks } from "./cutMarks";

const slotBottomLeft = (slot) => ({
  x: ARTWORK_LAYOUT.x + (slot.x_offset || 0),
  y: ARTWORK_LAYOUT.y + (slot.y_offset || 0),
});

/** Clip retangular (origem PDF) antes de carimbar a unit. */
const clipRect = (page, x, y, width, height) => {
  page.pushOperators(
    pushGraphicsState(),
    moveTo(x, y),
    lineTo(x + width, y),
    lineTo(x + width, y + height),
    lineTo(x, y + height),
    closePath(),
    clip(),
    endPath()
  );
};

/**
 * @param {ArrayBuffer|Uint8Array} overlayBytes — PDF só com texto/QR
 * @param {Array<{ bucket: string }>} pageCards — cartas na mesma ordem do overlay
 */
const composeSheetWithUnits = async (overlayBytes, pageCards) => {
  const overlayDoc = await PDFDocument.load(overlayBytes);
  const out = await PDFDocument.create();
  const overlayPages = await out.embedPdf(overlayDoc, overlayDoc.getPageIndices());

  const unitCache = new Map();
  const embedUnit = async (bucket) => {
    if (unitCache.has(bucket)) return unitCache.get(bucket);
    const bytes = await fetchUnitTemplateBytes(bucket);
    const [embedded] = await out.embedPdf(await PDFDocument.load(bytes), [0]);
    unitCache.set(bucket, embedded);
    return embedded;
  };

  for (let pageIndex = 0; pageIndex < overlayPages.length; pageIndex += 1) {
    const page = out.addPage([PAGE.width, PAGE.height]);
    const start = pageIndex * 10;
    const cardsOnPage = pageCards.slice(start, start + 10);
    const occupiedSlots = cardsOnPage.map((_, i) => slotForIndex(i));

    for (let i = 0; i < cardsOnPage.length; i += 1) {
      const card = cardsOnPage[i];
      const unit = await embedUnit(card.bucket || "majorca");
      const slot = occupiedSlots[i];
      const { x, y } = slotBottomLeft(slot);
      clipRect(page, x, y, ARTWORK_LAYOUT.width, ARTWORK_LAYOUT.height);
      page.drawPage(unit, {
        x,
        y,
        width: ARTWORK_LAYOUT.width,
        height: ARTWORK_LAYOUT.height,
      });
      page.pushOperators(popGraphicsState());
    }

    // Depois das molduras, antes do texto: ticks de corte nos slots ocupados.
    drawCutMarks(page, occupiedSlots);

    page.drawPage(overlayPages[pageIndex], {
      x: 0,
      y: 0,
      width: PAGE.width,
      height: PAGE.height,
    });
  }

  return out.save();
};

export { composeSheetWithUnits, slotBottomLeft };
